import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

const MIN_THUMB_HEIGHT = 28;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

const CustomScrollbar = forwardRef(function CustomScrollbar({
  as: Root = 'div',
  contentAs: Content = 'div',
  className = '',
  scrollerClassName = '',
  contentClassName = '',
  trackClassName = '',
  thumbClassName = '',
  style = {},
  children,
  ...rest
}, forwardedRef) {
  const scrollerRef = useRef(null);
  const contentRef = useRef(null);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const metricsFrameRef = useRef(0);
  const metricsRef = useRef({
    canScroll: false,
    trackHeight: 0,
    thumbHeight: 0,
    thumbTop: 0,
  });
  const [metrics, setMetrics] = useState(metricsRef.current);
  const [isDragging, setIsDragging] = useState(false);
  const scrollbarGap = metrics.canScroll ? 15 : 0;
  const setScrollerNode = useCallback(
    (node) => {
      scrollerRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef && typeof forwardedRef === 'object') {
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );

  const syncMetrics = useCallback(() => {
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller) return;

    const viewportHeight = scroller.clientHeight || 0;
    const trackHeight = track?.clientHeight || viewportHeight || 0;
    const scrollHeight = scroller.scrollHeight || 0;
    const scrollTop = scroller.scrollTop || 0;
    const scrollable = Math.max(0, scrollHeight - viewportHeight);

    if (scrollable <= 0 || trackHeight <= 0) {
      const next = {
        canScroll: false,
        trackHeight,
        thumbHeight: 0,
        thumbTop: 0,
      };
      metricsRef.current = next;
      setMetrics((previous) => (
        previous.canScroll === next.canScroll &&
        previous.trackHeight === next.trackHeight &&
        previous.thumbHeight === next.thumbHeight &&
        previous.thumbTop === next.thumbTop
          ? previous
          : next
      ));
      return;
    }

    const thumbHeight = clamp(
      Math.round((viewportHeight / scrollHeight) * trackHeight),
      MIN_THUMB_HEIGHT,
      trackHeight,
    );
    const draggable = Math.max(1, trackHeight - thumbHeight);
    const thumbTop = clamp(Math.round((scrollTop / scrollable) * draggable), 0, draggable);
    const next = {
      canScroll: true,
      trackHeight,
      thumbHeight,
      thumbTop,
    };
    metricsRef.current = next;
    setMetrics((previous) => (
      previous.canScroll === next.canScroll &&
      previous.trackHeight === next.trackHeight &&
      previous.thumbHeight === next.thumbHeight &&
      previous.thumbTop === next.thumbTop
        ? previous
        : next
    ));
  }, []);

  const scheduleMetricsSync = useCallback(() => {
    if (metricsFrameRef.current) return;

    const run = () => {
      metricsFrameRef.current = 0;
      syncMetrics();
    };

    metricsFrameRef.current =
      typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(run)
        : setTimeout(run, 0);
  }, [syncMetrics]);

  const onWindowPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    const scroller = scrollerRef.current;
    const current = metricsRef.current;
    if (!drag || !scroller || !current.canScroll) return;

    const draggable = Math.max(1, current.trackHeight - current.thumbHeight);
    const scrollable = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    if (scrollable <= 0) return;

    const deltaY = event.clientY - drag.startY;
    const nextScrollTop = clamp(drag.startScrollTop + (deltaY / draggable) * scrollable, 0, scrollable);
    scroller.scrollTop = nextScrollTop;
  }, []);

  const stopDragging = useCallback(() => {
    dragRef.current = null;
    setIsDragging(false);
    window.removeEventListener('pointermove', onWindowPointerMove);
    window.removeEventListener('pointerup', stopDragging);
    window.removeEventListener('pointercancel', stopDragging);
  }, [onWindowPointerMove]);

  const startDragging = useCallback(
    (event) => {
      const scroller = scrollerRef.current;
      if (!scroller || !metricsRef.current.canScroll) return;

      event.preventDefault();
      event.stopPropagation();
      dragRef.current = {
        startY: event.clientY,
        startScrollTop: scroller.scrollTop,
      };
      setIsDragging(true);
      window.addEventListener('pointermove', onWindowPointerMove);
      window.addEventListener('pointerup', stopDragging);
      window.addEventListener('pointercancel', stopDragging);
    },
    [onWindowPointerMove, stopDragging],
  );

  const onTrackPointerDown = useCallback((event) => {
    const scroller = scrollerRef.current;
    const current = metricsRef.current;
    if (!scroller || !current.canScroll) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickY = event.clientY - rect.top;
    const maxThumbTop = Math.max(1, rect.height - current.thumbHeight);
    const nextThumbTop = clamp(clickY - current.thumbHeight / 2, 0, maxThumbTop);
    const ratio = nextThumbTop / maxThumbTop;
    const scrollable = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    scroller.scrollTop = ratio * scrollable;
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller) return;

    scheduleMetricsSync();
    const onScroll = () => scheduleMetricsSync();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', scheduleMetricsSync);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMetricsSync);
      resizeObserver.observe(scroller);
      if (track) resizeObserver.observe(track);
      if (contentRef.current) resizeObserver.observe(contentRef.current);
    }

    let mutationObserver = null;
    if (typeof MutationObserver !== 'undefined' && contentRef.current) {
      mutationObserver = new MutationObserver(scheduleMetricsSync);
      mutationObserver.observe(contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', scheduleMetricsSync);
      if (metricsFrameRef.current) {
        if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(metricsFrameRef.current);
        } else {
          clearTimeout(metricsFrameRef.current);
        }
        metricsFrameRef.current = 0;
      }
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [scheduleMetricsSync]);

  useEffect(() => () => stopDragging(), [stopDragging]);

  return (
    <Root
      className={`relative min-h-0 ${className}`.trim()}
      style={{ ...style, '--custom-scrollbar-gap': `${scrollbarGap}px` }}
      {...rest}
    >
      <div
        ref={setScrollerNode}
        className={`custom-scrollbar-native relative h-full overflow-y-auto ${scrollerClassName}`.trim()}
      >
        <Content
          ref={contentRef}
          className={`${contentClassName} mr-[var(--custom-scrollbar-gap)]`.trim()}
        >
          {children}
        </Content>
      </div>
      <div
        ref={trackRef}
        className={`absolute inset-y-1 right-0 w-2 rounded-full bg-transparent transition-opacity duration-200 ${
          metrics.canScroll ? 'opacity-100' : 'pointer-events-none opacity-0'
        } overflow-hidden ${trackClassName}`.trim()}
        onPointerDown={onTrackPointerDown}
        aria-hidden="true"
      >
        <div
          className={`absolute left-0.5 right-0.5 rounded-full bg-[var(--dash-black)] transition-[transform,filter] duration-150 ${
            isDragging ? 'cursor-grabbing brightness-110' : 'cursor-grab'
          } ${thumbClassName}`.trim()}
          style={{
            height: `${metrics.thumbHeight}px`,
            transform: `translateY(${metrics.thumbTop}px)`,
          }}
          onPointerDown={startDragging}
        />
      </div>
    </Root>
  );
});

export default CustomScrollbar;
